import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type { ClaimedDomain } from "@backstory/protocol";
import { request } from "../../api";
import { OrgLayout } from "../../components/Shell";
import { CopyButton, ConfirmDialog, DataTable, Field, PageHeader, RowMenu, useToast } from "../../components/ui";

const enc = encodeURIComponent;
const domainsKey = (orgId: string) => ["domains", orgId] as const;

export function DomainsPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const domains = useQuery({ queryKey: domainsKey(orgId), queryFn: () => request<ClaimedDomain[]>(`/v1/orgs/${enc(orgId)}/domains`) });
  const [domain, setDomain] = useState("");
  const [removing, setRemoving] = useState<{ id: string; domain: string } | null>(null);
  const claim = useMutation({
    mutationFn: () => request<ClaimedDomain>(`/v1/orgs/${enc(orgId)}/domains`, { method: "POST", body: { domain: domain.trim().toLowerCase() } }),
    onSuccess: () => {
      setDomain("");
      void qc.invalidateQueries({ queryKey: domainsKey(orgId) });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const verify = useMutation({
    mutationFn: (id: string) => request<ClaimedDomain>(`/v1/orgs/${enc(orgId)}/domains/${enc(id)}/verify`, { method: "POST" }),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: domainsKey(orgId) });
      toast.push(d.verified ? "success" : "info", d.verified ? `${d.domain} verified.` : "TXT record not found yet. DNS can take a few minutes to propagate.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const del = useMutation({
    mutationFn: (id: string) => request<void>(`/v1/orgs/${enc(orgId)}/domains/${enc(id)}`, { method: "DELETE" }),
    onSuccess: () => {
      setRemoving(null);
      void qc.invalidateQueries({ queryKey: domainsKey(orgId) });
      toast.push("success", "Domain removed.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const valid = /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain.trim());

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader title="Domains" />
      <div className="card section">
        <h2>Claim a domain</h2>
        <div className="actions-row" style={{ alignItems: "flex-end" }}>
          <Field label="Domain" error={domain && !valid ? "Enter a bare domain like example.com" : undefined}>
            {(p) => <input {...p} className="input" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="example.com" style={{ width: 280 }} />}
          </Field>
          <button className="btn primary" style={{ marginBottom: 16 }} onClick={() => claim.mutate()} disabled={!valid || claim.isPending}>
            Claim
          </button>
        </div>
      </div>
      <div className="card stack-table" style={{ marginTop: 16 }}>
        <DataTable
          ariaLabel="Claimed domains"
          rows={domains.data ?? []}
          loading={domains.isPending}
          rowKey={(d) => d.id}
          empty="No claimed domains."
          columns={[
            { key: "d", header: "Domain", label: "Domain", render: (d) => <strong>{d.domain}</strong> },
            { key: "s", header: "Status", label: "Status", render: (d) => (d.verified ? <span className="badge ok">verified</span> : <span className="badge warn">pending DNS</span>) },
            {
              key: "txt",
              header: "TXT record to publish",
              label: "TXT record",
              render: (d) =>
                d.verified ? (
                  <span className="muted">—</span>
                ) : (
                  <div className="small">
                    <div className="mono">{d.txtRecordName} <CopyButton value={d.txtRecordName} label="copy name" /></div>
                    <div className="mono">{d.txtRecordValue} <CopyButton value={d.txtRecordValue} label="copy value" /></div>
                  </div>
                ),
            },
            {
              key: "act",
              header: "",
              align: "right",
              render: (d) => (
                <RowMenu label={`Actions for ${d.domain}`}>
                  {!d.verified && <button type="button" role="menuitem" onClick={() => verify.mutate(d.id)} disabled={verify.isPending}>Verify</button>}
                  <button type="button" role="menuitem" className="danger" onClick={() => setRemoving({ id: d.id, domain: d.domain })}>Remove</button>
                </RowMenu>
              ),
            },
          ]}
        />
      </div>
      <ConfirmDialog
        open={!!removing}
        danger
        title="Remove domain"
        confirmLabel="Remove domain"
        busy={del.isPending}
        onClose={() => { if (!del.isPending) setRemoving(null); }}
        onConfirm={() => removing && del.mutate(removing.id)}
      >
        <p style={{ margin: 0 }}><strong>{removing?.domain}</strong> is no longer claimed. Sign-in for that domain is unaffected until you claim it again.</p>
      </ConfirmDialog>
    </OrgLayout>
  );
}
